import { useEffect, useState } from 'react';
import { AppShell, type PageKey } from './AppShell';
import { AuthPage } from '../features/auth/AuthPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { TransactionForm } from '../features/transactions/TransactionForm';
import { UpcomingPage } from '../features/plans/UpcomingPage';
import { RecordsPage } from '../features/records/RecordsPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { SetupPage } from '../features/setup/SetupPage';
import { MonthlyClosePage } from '../features/monthlyClose/MonthlyClosePage';
import { FinancialHealthPage } from '../features/health/FinancialHealthPage';
import { useAuth } from '../hooks/useAuth';
import { useMasters } from '../hooks/useMasters';
import { useNotifications } from '../hooks/useNotifications';
import { Card, Modal, Skeleton } from '../components/UI';
import { firebaseConfigPreview } from '../firebase/config';
import { listenUpcomingPlans } from '../firebase/repo';
import { displayDate, formatMoney, todayISO } from '../utils/date';
import type { Plan } from '../types';

function FirebaseSetupError({ message }: { message: string }) {
  return <main className="auth-page">
    <Card>
      <h1>Firebase setup issue</h1>
      <p className="muted">{message}</p>
      <div className="detail">
        {Object.entries(firebaseConfigPreview).map(([key, value]) => (
          <p key={key}><b>{key}</b><span>{value}</span></p>
        ))}
      </div>
      <div className="warning">
        Check that <b>.env</b> exists in the project root, not inside <b>functions</b>, and restart Vite after changing env values.
      </div>
      <p className="muted">Expected project ID: <b>sg-pfs</b></p>
    </Card>
  </main>;
}

export function App() {
  const auth = useAuth();
  const [page, setPage] = useState<PageKey>(() => initialPageFromHash());
  const [duePlans, setDuePlans] = useState<Plan[]>([]);
  const [dueModalOpen, setDueModalOpen] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const { masters, contacts, grouped } = useMasters(auth.user?.uid);
  const notificationState = useNotifications(auth.user?.uid);
  const uid = auth.user?.uid || '';

  useEffect(() => {
    if (!uid) {
      setDuePlans([]);
      setDueModalOpen(false);
      return undefined;
    }
    return listenUpcomingPlans(uid, (plans) => {
      const today = todayISO();
      const rows = plans.filter((plan) => plan.status === 'active' && plan.active !== false && (plan.nextDueDate || plan.dueDate) === today);
      const key = `pfs_due_today_${uid}_${today}`;
      setDuePlans(rows);
      if (rows.length && localStorage.getItem(key) !== 'hidden') setDueModalOpen(true);
    });
  }, [uid]);

  if (auth.loading) return <main className="auth-page"><Skeleton /></main>;
  if (auth.error) return <FirebaseSetupError message={auth.error} />;
  if (!auth.user) return <AuthPage auth={auth} />;

  const dueStorageKey = `pfs_due_today_${uid}_${todayISO()}`;
  const navigate = (next: PageKey) => {
    if (next === 'reports' && typeof window !== 'undefined') {
      window.history.pushState(null, '', '#reports');
      window.dispatchEvent(new Event('hashchange'));
    }
    setPage(next);
  };
  const hideDueToday = () => {
    localStorage.setItem(dueStorageKey, 'hidden');
    setDueModalOpen(false);
  };
  return <AppShell page={page} onPage={navigate} onLogout={auth.logout} notifications={notificationState.notifications} unreadCount={notificationState.unreadCount} notificationsLoading={notificationState.loading} notificationCenterOpen={notificationCenterOpen} onNotificationCenterOpenChange={setNotificationCenterOpen} onNotificationRead={notificationState.markRead} onNotificationsReadAll={notificationState.markAllRead} onNotificationDismiss={notificationState.dismiss} onNotificationsRefresh={notificationState.refresh}>
    {page === 'home' && <DashboardPage uid={uid} onNavigate={navigate} notifications={notificationState.notifications} unreadCount={notificationState.unreadCount} onOpenNotifications={() => setNotificationCenterOpen(true)} />}
    {page === 'upcoming' && <UpcomingPage uid={uid} grouped={grouped} />}
    {page === 'add' && <TransactionForm uid={uid} grouped={grouped} contacts={contacts} />}
    {page === 'records' && <RecordsPage uid={uid} grouped={grouped} contacts={contacts} />}
    {page === 'reports' && <ReportsPage uid={uid} />}
    {page === 'health' && <FinancialHealthPage uid={uid} onNavigate={navigate} />}
    {page === 'monthlyClose' && <MonthlyClosePage uid={uid} onNavigate={navigate} />}
    {page === 'setup' && <SetupPage uid={uid} email={auth.user.email || ''} masters={masters} contacts={contacts} grouped={grouped} />}
    <Modal open={dueModalOpen} title="Payments due today" onClose={hideDueToday}>
      <p className="muted">Review today's active plans. Mark paid from Upcoming so account deduction and confirmation stay consistent.</p>
      {duePlans.map((plan) => <div className="history-row" key={plan.id}><div><b>{plan.title}</b><span>{displayDate(plan.nextDueDate || plan.dueDate)} / {plan.category} / {plan.paidFromAccountName || 'Unassigned'}</span></div><strong>{formatMoney(plan.amount)}</strong></div>)}
      <div className="confirm-actions"><button className="btn" onClick={hideDueToday}>Don't show today</button><button className="btn" onClick={() => setDueModalOpen(false)}>Remind later</button><button className="btn primary" onClick={() => { navigate('upcoming'); setDueModalOpen(false); }}>Open Upcoming</button></div>
    </Modal>
  </AppShell>;
}

function initialPageFromHash(): PageKey {
  if (typeof window === 'undefined') return 'home';
  if (window.location.hash.startsWith('#reports')) return 'reports';
  if (window.location.hash.startsWith('#health')) return 'health';
  if (window.location.hash.startsWith('#settings')) return 'setup';
  return 'home';
}
